/*
 * Babyfair – product page pricing (single version) – v2 for "Fixes | Live Copy"
 * Also safe on the live theme: the draft-only parts (Select Options guard, delivery text)
 * only switch on when validateButtonState() / window.bfPdpConfig exist.
 * Replaces the 4 inline scripts (5 copies) in the main product section that handled:
 *   Spend & Save banner, charity line, Klarna / Clearpay / Zopa badges + table,
 *   Zopa drawer, and variant stock/button state.
 *
 * Key changes vs the old scripts:
 *   - cart.js is fetched once on load, then only after a real cart change
 *     (/cart/add, /cart/change, /cart/update, /cart/clear). No more polling loop.
 *   - Our own cart.js request bypasses the fetch hook, so it can't trigger itself.
 *   - All triggers (price observer, add-on clicks, variant change) are debounced into one render.
 *   - £0 failsafe only fires on an actual £0 price (the old check also matched £100.00, £300.00 etc.).
 *   - No hardcoded £269.99 fallback; uses data-base-price instead.
 *   - Works with both the ID-based and class-based markup (desktop + mobile).
 */
(function () {
  'use strict';
  if (window.__bfPdpPricing) return; // guard in case the section renders twice
  window.__bfPdpPricing = true;

  // ---------- Config ----------
  var PRICE_SEL = '.custom-variant-price-value';
  var ADDON_SEL = '.custom-variant-item, .dynamic-mattress-item, .add-cart-main-assembly, .btn-assembly-remove';
  var CART_CHANGE = /\/cart\/(add|change|update|clear)/;
  var CHARITY_RATE = 0.015;
  var SS_TIERS = [ // highest first
    { min: 1000, off: 100 },
    { min: 750, off: 75 },
    { min: 500, off: 50 },
    { min: 250, off: 25 }
  ];
  var ZOPA_TERMS = [ // highest first
    { months: 12, min: 600 },
    { months: 9, min: 450 },
    { months: 6, min: 300 },
    { months: 3, min: 200 }
  ];
  var CFG = window.bfPdpConfig || {};
  // Draft theme passes the delivery line from Liquid; live theme falls back to the old text
  var STOCK_IN = CFG.deliveryHtml || '<b>Hurry</b> Last few left in stock';
  var STOCK_OUT = '<b style="color:#6c3fa0;font-weight:600;">Out of stock</b>';
  var STOCK_NA = '<b style="color:#6c3fa0;">Unavailable</b>';
  var STOCK_SELECT = '<b style="color:#666;">Select options to see availability</b>';

  var GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var nativeFetch = window.fetch.bind(window);

  var cartTotal = 0;
  var renderTimer = null;
  var cartTimer = null;

  function all(sel) { return document.querySelectorAll(sel); }
  function money(n) { return '£' + n.toFixed(2); }
  function zopaMonthly(price, months) { return (Math.floor(price / months - 0.01) + 0.99).toFixed(2); }

  // ---------- Data ----------
  function getPrice() {
    var el = document.querySelector(PRICE_SEL);
    if (!el) return 0;
    var p = parseFloat(el.textContent.replace(/[^\d.]/g, ''));
    if (!isNaN(p) && p > 0) return p;
    var base = parseFloat(el.getAttribute('data-base-price')) / 100;
    return base > 0 ? base : 0;
  }

  function refreshCart() {
    return nativeFetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cartTotal = (cart.total_price || 0) / 100; })
      .catch(function () { /* keep last known total */ });
  }

  // ---------- Charity ----------
  function updateCharity(price) {
    all('.charity-donation-amount, #charity-donation-amount').forEach(function (el) {
      el.textContent = GBP.format(price * CHARITY_RATE);
    });
    all('.charity-you-pay, #charity-you-pay').forEach(function (el) {
      el.textContent = GBP.format(price);
    });
  }

  // ---------- Spend & Save ----------
  function updateSpendAndSave(price) {
    var texts = all('.ss-dynamic-text, #ss-dynamic-text');
    var fills = all('.ss-progress-fill, #ss-progress-fill');
    var headings = all('.ss-dynamic-heading, #ss-dynamic-heading');
    if (!texts.length && !fills.length) return;

    var total = cartTotal + price;
    var unlocked = 0, next = 250, save = 25, maxed = false;
    for (var i = 0; i < SS_TIERS.length; i++) {
      if (total >= SS_TIERS[i].min) {
        unlocked = SS_TIERS[i].off;
        if (i === 0) { maxed = true; } else { next = SS_TIERS[i - 1].min; save = SS_TIERS[i - 1].off; }
        break;
      }
    }

    var html, heading, pct;
    if (maxed) {
      html = '🎉 <span style="color:#331B66;">You\'ve unlocked the maximum £100 off!</span>';
      heading = '🎉 You unlocked £100 off!';
      pct = 100;
    } else {
      var progress = '£' + total.toFixed(0) + ' of £' + next + ' &middot; £' + Math.ceil(next - total) + ' to £' + save + ' off';
      pct = Math.min((total / next) * 100, 100);
      if (unlocked > 0) {
        html = '🎉 <span style="color:#331B66;">You unlocked £' + unlocked + ' off!</span> &middot; ' + progress;
        heading = '🎉 You unlocked £' + unlocked + ' off!';
      } else {
        html = progress;
        heading = 'Spend & Save — up to £100 off';
      }
    }

    texts.forEach(function (el) { el.innerHTML = html; });
    fills.forEach(function (el) { el.style.width = pct + '%'; });
    headings.forEach(function (el) { el.textContent = heading; });
  }

  // ---------- Klarna / Clearpay / Zopa ----------
  function updatePaymentBadges(price) {
    var klarna = (price / 3).toFixed(2);
    var clearpay = (price / 4).toFixed(2);
    all('.klarna-text, #klarna-text, .klarna-text-target').forEach(function (el) {
      el.innerHTML = 'Make 3 payments of £' + klarna + '. <strong>Klarna</strong>';
    });
    all('.clearpay-text, #clearpay-text, .clearpay-text-target').forEach(function (el) {
      el.innerHTML = 'or 4 interest-free payments of £' + clearpay + ' with <strong>Clearpay</strong>.';
    });

    var maxTerm = null;
    for (var i = 0; i < ZOPA_TERMS.length; i++) {
      if (price >= ZOPA_TERMS[i].min) { maxTerm = ZOPA_TERMS[i]; break; }
    }
    var moreInfo = '<br><span style="color:#666;font-size:11px;text-decoration:underline;">Click for more info</span>';
    all('.zopa-text, #zopa-text, .zopa-text-target').forEach(function (el) {
      el.innerHTML = maxTerm
        ? 'Or from £' + zopaMonthly(price, maxTerm.months) + ' p/m, 0% APR. <strong>Zopa</strong>' + moreInfo
        : 'Spend at least £200 to spread the cost. <strong>Zopa</strong>' + moreInfo;
    });

    // Zopa drawer table (supports #z-row-N and .z-row-N markup)
    ZOPA_TERMS.forEach(function (term) {
      var m = term.months;
      all('#z-row-' + m + ', .z-row-' + m).forEach(function (row) {
        var totalEl = row.querySelector('#z-total-' + m + ', .z-total-' + m) || document.getElementById('z-total-' + m);
        var monthEl = row.querySelector('#z-month-' + m + ', .z-month-' + m) || document.getElementById('z-month-' + m);
        if (!totalEl || !monthEl) return;
        monthEl.textContent = '£' + zopaMonthly(price, m);
        if (price >= term.min) {
          row.style.opacity = '1';
          row.style.color = '#113430';
          totalEl.innerHTML = money(price);
        } else {
          row.style.opacity = '0.5';
          row.style.color = '#a8aab3';
          totalEl.innerHTML = 'Only available<br>above ' + money(term.min);
        }
      });
    });
  }

  // ---------- Render (debounced) ----------
  function render() {
    var price = getPrice();
    if (price <= 0) return;
    updateCharity(price);
    updateSpendAndSave(price);
    updatePaymentBadges(price);
  }
  function scheduleRender(delay) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, delay || 50);
  }
  function onCartChanged() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(function () { refreshCart().then(render); }, 300);
  }

  // Backwards compatibility for any other theme code still calling these
  window.updateSpendAndSave = function () { scheduleRender(); };
  window.updatePaymentBadges = function () { scheduleRender(); };

  // ---------- Cart change hooks (fetch + XHR) ----------
  window.fetch = function (input, init) {
    return nativeFetch(input, init).then(function (res) {
      if (CART_CHANGE.test(res.url || '')) onCartChanged();
      return res;
    });
  };
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_CHANGE.test(String(url))) this.addEventListener('load', onCartChanged);
    return xhrOpen.apply(this, arguments);
  };

  // ---------- Price watcher ----------
  function watchPrice() {
    var el = document.querySelector(PRICE_SEL);
    if (!el) return;
    new MutationObserver(function () {
      // Failsafe: theme occasionally flashes £0.00 – restore the base price
      if (/^£?\s*0(\.00)?$/.test(el.textContent.trim())) {
        var base = parseFloat(el.getAttribute('data-base-price')) / 100;
        if (base > 0) { el.textContent = GBP.format(base); return; }
      }
      scheduleRender();
    }).observe(el, { childList: true, characterData: true, subtree: true });
  }

  // ---------- Variant change: stock text + add-to-cart button ----------
  function setButton(state) { // 'available' | 'soldout' | 'unavailable'
    var btn = document.querySelector('.variant-cart-add-btn');
    if (!btn) return;
    var label = btn.querySelector('.btn-text');
    btn.classList.toggle('sold-out-btn', state !== 'available');
    if (label) label.textContent = state === 'available' ? 'Add to cart' : state === 'soldout' ? 'Sold out' : 'Unavailable';
  }
  function setStock(html) {
    all('#dynamic-stock-text, .dynamic-stock-text').forEach(function (el) { el.innerHTML = html; });
  }

  function hasOptionFlow() { return typeof window.validateButtonState === 'function'; }

  document.addEventListener('variant:changed', function (e) {
    var variant = e.detail && e.detail.variant;
    if (variant && variant.price > 0) window.currentVariantPrice = variant.price / 100;
    scheduleRender(); // banner + badges always follow the price

    // Draft theme: validateButtonState() owns the button until all options are chosen
    if (hasOptionFlow() && !window.validateButtonState()) return;

    var hasVariants = all('.custom-variant-item').length > 0;
    if (!variant || variant.price === 0) {
      if (!hasVariants) return; // single-variant product
      var priceEl = document.querySelector(PRICE_SEL);
      if (priceEl) priceEl.innerHTML = 'Unavailable';
      setStock(STOCK_NA);
      setButton('unavailable');
      return;
    }

    if (typeof window.updatezopaPopup === 'function') window.updatezopaPopup();
    setStock(variant.available ? STOCK_IN : STOCK_OUT);
    setButton(variant.available ? 'available' : 'soldout');
  });

  // Draft theme only: stop the theme's own script flipping the button back to "Add to cart"
  // before all options are chosen, or when the chosen variant is out of stock.
  var variantsCache = null;
  function selectedVariant() {
    if (!variantsCache) {
      var el = document.getElementById('product-variants-data');
      if (!el) return null;
      try { variantsCache = JSON.parse(el.textContent).variants || []; } catch (err) { return null; }
    }
    var picked = [1, 2, 3].map(function (pos) {
      var input = document.querySelector('input[data-option-position="' + pos + '"]:checked');
      return input ? input.getAttribute('data-option-value') : null;
    });
    if (!picked[0] && !picked[1] && !picked[2]) return variantsCache[0] || null;
    return variantsCache.find(function (v) {
      return (!picked[0] || v.option1 === picked[0]) && (!picked[1] || v.option2 === picked[1]) && (!picked[2] || v.option3 === picked[2]);
    }) || null;
  }

  function guardAddToCart() {
    var btn = document.querySelector('.variant-cart-add-btn');
    if (!btn || !hasOptionFlow()) return;
    var opts = { attributes: true, childList: true, subtree: true };
    var obs = new MutationObserver(function () {
      var hasOpts = all('.custom-variant-item').length > 0;
      var locked = hasOpts && ((typeof window.areAllOptionsSelected === 'function' && !window.areAllOptionsSelected()) || !window.finalTabInteracted);
      var label = btn.querySelector('.btn-text');
      var want = null, stock = null;
      if (locked) { want = 'Select Options'; stock = STOCK_SELECT; }
      else {
        var v = selectedVariant();
        if (v && !v.available) { want = 'Sold out'; stock = STOCK_OUT; }
      }
      if (!want) return;
      if (btn.classList.contains('sold-out-btn') && label && label.textContent === want) return;
      obs.disconnect(); // avoid reacting to our own change
      btn.classList.add('sold-out-btn');
      if (label) label.textContent = want;
      setStock(stock);
      obs.observe(btn, opts);
    });
    obs.observe(btn, opts);
  }

  // ---------- Clicks: add-ons + Zopa drawer (one delegated listener) ----------
  function setZopaDrawer(open) {
    all('.zopa-drawer, #zopa-drawer').forEach(function (d) { d.classList.toggle('active', open); });
    all('.zopa-overlay, #zopa-overlay').forEach(function (o) { o.classList.toggle('active', open); });
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (t.closest('.zopa-trigger-btn, #zopa-trigger')) {
      e.preventDefault();
      setZopaDrawer(true);
      return;
    }
    if (t.closest('.zopa-close, #zopa-close, .zopa-overlay, #zopa-overlay')) {
      setZopaDrawer(false);
      return;
    }
    if (t.closest(ADDON_SEL)) {
      // Let the theme recalculate the price first
      setTimeout(function () {
        render();
        // Refresh the mattress list so sizes stay accurate
        var filter = document.querySelector('.f-filter-btn.active') || document.querySelector('.f-filter-btn[data-firmness="all"]');
        if (filter) filter.click();
      }, 150);
    }
  });

  // ---------- Init ----------
  function init() {
    watchPrice();
    guardAddToCart();
    render();                    // immediate paint from product price
    refreshCart().then(render);  // one cart fetch, then repaint with cart total
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
